import { useState, useEffect } from 'react'
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { apiClient } from '@/services/apiClient'
import { supabase } from '@/lib/supabase'

interface BatchCreateUsersModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  operatorId: string
}

interface UserCreationConfig {
  cost: number
  bonus: number
}

interface OperatorBalance {
  balance: number
  commission_balance: number
}

export default function BatchCreateUsersModal({ isOpen, onClose, onSuccess, operatorId }: BatchCreateUsersModalProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [batchCount, setBatchCount] = useState(10)
  const [initialBalance, setInitialBalance] = useState(0)
  const [batchResult, setBatchResult] = useState<any>(null)
  const [config, setConfig] = useState<UserCreationConfig>({ cost: 100, bonus: 50 })
  const [operatorBalance, setOperatorBalance] = useState<OperatorBalance | null>(null)
  const [isLoadingBalance, setIsLoadingBalance] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchConfig()
      fetchOperatorBalance()
    }
  }, [isOpen, operatorId])

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('system_parameters')
        .select('param_value')
        .eq('param_key', 'user_creation_config')
        .single()

      if (data && data.param_value) {
        const parsed = typeof data.param_value === 'string' 
          ? JSON.parse(data.param_value) 
          : data.param_value
        setConfig(parsed)
      }
    } catch (err) {
      console.error('Failed to fetch config:', err)
    }
  }

  const fetchOperatorBalance = async () => {
    try {
      setIsLoadingBalance(true)
      
      // 先获取 operator_id (从 admin_users 表)
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id')
        .eq('id', operatorId)
        .single()

      if (adminError || !adminUser?.operator_id) {
        console.error('Failed to fetch admin user:', adminError)
        return
      }

      // 获取运营商余额
      const { data, error } = await supabase
        .from('operator_accounts')
        .select('balance, commission_balance')
        .eq('operator_id', adminUser.operator_id)
        .single()

      if (error) {
        console.error('Failed to fetch operator balance:', error)
        return
      }

      setOperatorBalance(data)
    } catch (err) {
      console.error('Failed to fetch operator balance:', err)
    } finally {
      setIsLoadingBalance(false)
    }
  }

  const totalCost = config.cost * batchCount
  const totalBonus = config.bonus * batchCount
  const netCost = totalCost - totalBonus
  const isBalanceSufficient = operatorBalance ? operatorBalance.balance >= totalCost : true

  const handleBatchCreate = async () => {
    try {
      setIsCreating(true)
      setError(null)

      // 调用批量创建 API
      console.log('=== 批量创建用户请求 ===')
      console.log('operatorId:', operatorId)
      console.log('batchCount:', batchCount)
      console.log('initialBalance:', initialBalance)
      
      const response = await apiClient.batchCreateUsers(operatorId, batchCount, initialBalance)
      
      console.log('=== 批量创建用户响应 ===')
      console.log('response:', JSON.stringify(response, null, 2))

      if (!response.success) {
        console.error('批量创建失败:', response.message)
        setError(response.message || '批量创建失败')
        return
      }

      // 保存结果
      console.log('✅ 批量创建成功，设置结果:', response.data)
      setBatchResult(response.data)
      // 不要立即调用 onSuccess()，等用户关闭模态框时再刷新列表
    } catch (err: any) {
      console.error('批量创建异常:', err)
      setError(err.message || '批量创建失败')
    } finally {
      setIsCreating(false)
    }
  }

  const handleClose = () => {
    // 如果创建了用户，关闭时刷新列表
    if (batchResult) {
      onSuccess()
    }
    setBatchResult(null)
    setError(null)
    setBatchCount(10)
    setInitialBalance(0)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">批量创建用户</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {!batchResult ? (
            <>
              {/* 余额信息卡片 */}
              {operatorBalance && (
                <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">当前余额</p>
                      <p className="text-2xl font-bold text-blue-600">
                        ${operatorBalance.balance.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 mb-1">佣金余额</p>
                      <p className="text-lg font-semibold text-green-600">
                        ${operatorBalance.commission_balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {!isBalanceSufficient && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-700">
                        ⚠️ 余额不足！需要 ${totalCost.toFixed(2)}，当前余额 ${operatorBalance.balance.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isLoadingBalance && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500">加载余额信息...</p>
                </div>
              )}

              <div className="space-y-4 mb-6">
                {/* 成本和返佣信息卡片 - 简化版 */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600">成本: <span className="font-semibold text-red-600">${totalCost}</span></span>
                      <span className="text-gray-600">返佣: <span className="font-semibold text-green-600">${totalBonus}</span></span>
                      <span className="text-gray-600">净成本: <span className="font-semibold text-purple-600">${netCost}</span></span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    创建数量
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={batchCount}
                    onChange={(e) => setBatchCount(parseInt(e.target.value) || 10)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="输入要创建的用户数量 (1-100)"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    将批量创建 {batchCount} 个测试用户
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    初始余额 (可选)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={initialBalance}
                    onChange={(e) => setInitialBalance(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="输入初始余额"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    每个用户将获得 {initialBalance} USDT 的初始余额
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-xs text-yellow-800">
                    系统将自动生成随机邮箱和密码，创建完成后会显示所有用户的登录信息，请妥善保存。
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleBatchCreate}
                  disabled={isCreating || batchCount < 1 || batchCount > 100 || !isBalanceSufficient}
                  className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <div className="loading-spinner w-4 h-4 mr-2"></div>
                      创建中...
                    </>
                  ) : (
                    `创建 ${batchCount} 个用户（净成本 $${netCost}）`
                  )}
                </button>
                <button
                  onClick={handleClose}
                  disabled={isCreating}
                  className="flex-1 btn btn-secondary"
                >
                  取消
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <CheckCircleIcon className="w-5 h-5 inline mr-2" />
                    批量创建完成！成功创建 {batchResult.summary?.success || 0} 个用户
                    {batchResult.summary?.failed > 0 && `, 失败 ${batchResult.summary.failed} 个`}
                  </p>
                </div>

                {batchResult.created_users && batchResult.created_users.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">创建成功的用户：</h3>
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">邮箱</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">姓名</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">密码</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {batchResult.created_users.map((user: any, index: number) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-gray-900 font-mono text-xs">{user.email}</td>
                              <td className="px-4 py-2 text-gray-900">{user.full_name}</td>
                              <td className="px-4 py-2 text-gray-900 font-mono text-xs">{user.password}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-2 text-xs text-red-600">
                      ⚠️ 请立即复制保存这些登录信息，关闭后将无法再次查看密码！
                    </p>
                  </div>
                )}

                {batchResult.errors && batchResult.errors.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-red-900 mb-2">创建失败的用户：</h3>
                    <div className="max-h-48 overflow-y-auto border border-red-200 rounded-md bg-red-50 p-3">
                      {batchResult.errors.map((error: any, index: number) => (
                        <div key={index} className="text-xs text-red-800 mb-1">
                          #{error.index}: {error.email || '未知'} - {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // 复制所有用户信息到剪贴板
                    const text = batchResult.created_users
                      .map((u: any) => `${u.email}\t${u.full_name}\t${u.password}`)
                      .join('\n')
                    navigator.clipboard.writeText(text)
                    alert('用户信息已复制到剪贴板')
                  }}
                  className="flex-1 btn btn-primary"
                >
                  复制所有用户信息
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 btn btn-secondary"
                >
                  关闭
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
